import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';

import { useTranslation, Trans } from 'react-i18next';
import InviteUserDialog from './InviteUserDialog';
import {
  listCompanyInvitations,
  cancelCompanyInvitation,
  readAxiosDetail,
  type CompanyInvitation,
} from './api_calls';

import { Icon } from '@liftango/ui-icons';
export default function CompanyInvitationsTab() {
  const { t } = useTranslation('company');

  const formatRelativeExpiry = (expiresAt: string): string => {
    const diff = new Date(expiresAt).getTime() - Date.now();
    if (diff <= 0) return t('invitations.expiry.expired');
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    if (days === 1) return t('invitations.expiry.inOneDay');
    return t('invitations.expiry.inDays', { count: days });
  };

  const [invitations, setInvitations] = useState<CompanyInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<CompanyInvitation | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  const load = async () => {
    try {
      const data = await listCompanyInvitations();
      setInvitations(data);
    } catch (e) {
      setError(readAxiosDetail(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const handleCancel = async () => {
    if (!cancelTarget) return;
    setCancelLoading(true);
    try {
      await cancelCompanyInvitation(cancelTarget.invitation_id);
      setInvitations((prev) => prev.filter((i) => i.invitation_id !== cancelTarget.invitation_id));
      setCancelTarget(null);
    } catch (e) {
      setError(readAxiosDetail(e));
    } finally {
      setCancelLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          startIcon={<Icon size={20} name="plus" />}
          variant="contained"
          size="small"
          onClick={() => setInviteOpen(true)}
        >
          {t('invitations.inviteUser')}
        </Button>
      </Box>

      {invitations.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          {t('invitations.empty')}
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('invitations.columns.email')}</TableCell>
              <TableCell>{t('invitations.columns.role')}</TableCell>
              <TableCell>{t('invitations.columns.expires')}</TableCell>
              <TableCell>{t('invitations.columns.sent')}</TableCell>
              <TableCell align="right">{t('invitations.columns.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invitations.map((inv) => (
              <TableRow key={inv.invitation_id}>
                <TableCell>{inv.email}</TableCell>
                <TableCell>
                  <Chip
                    label={inv.company_role === 'company_admin' ? t('invitations.roleLabels.admin') : t('invitations.roleLabels.member')}
                    size="small"
                    color={inv.company_role === 'company_admin' ? 'primary' : 'default'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{formatRelativeExpiry(inv.expires_at)}</TableCell>
                <TableCell>{new Date(inv.created_at).toLocaleDateString()}</TableCell>
                <TableCell align="right">
                  <Button size="small" color="error" onClick={() => setCancelTarget(inv)}>
                    {t('invitations.actions.cancel')}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <InviteUserDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onCreated={() => void load()}
      />

      {/* Cancel confirmation */}
      <Dialog open={!!cancelTarget} onClose={() => setCancelTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('invitations.cancelDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography>
            <Trans
              i18nKey="invitations.cancelDialog.message"
              t={t}
              values={{ email: cancelTarget?.email ?? '' }}
              components={{ bold: <strong /> }}
            />
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelTarget(null)} disabled={cancelLoading}>{t('invitations.cancelDialog.keep')}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void handleCancel()}
            disabled={cancelLoading}
          >
            {cancelLoading ? <CircularProgress size={18} /> : t('invitations.cancelDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
